/*
 * BioIndex
 * Copyright (c) 2026 BioIndex™. All rights reserved.
 * Unauthorized copying, scraping, redistribution, or commercial reuse is prohibited.
 */

import { Link } from "react-router-dom";
import PolicyLanguageToggle, {
  usePolicyLanguage,
} from "../components/PolicyLanguageToggle";

const copy = {
  es: {
    title: "Aviso de derechos de autor",
    updated: "Última actualización",
    introduction:
      "Salvo que se indique lo contrario, el software, diseño y contenido creativo original que conforman BioIndex™ están protegidos por derechos de autor. El contenido cargado por los usuarios permanece bajo la propiedad de sus respectivos titulares.",
    returnHome: "Volver a BioIndex",
    rights: "Todos los derechos reservados.",
    sections: [
      {
        title: "Propiedad intelectual de BioIndex",
        text: "La interfaz, el código fuente, la organización de bases de datos, los textos originales, ilustraciones, insignias, cuestionarios, logotipos y demás recursos creativos originales de BioIndex™ están protegidos por derechos de autor y otras leyes de propiedad intelectual.",
      },
      {
        title: "Contenido propiedad de los usuarios",
        text: "Las fotografías y demás contenido original cargado por los usuarios permanecen bajo la propiedad de sus respectivos titulares. Al cargar contenido, los usuarios otorgan a BioIndex permiso para mostrarlo, organizarlo y utilizarlo dentro de la plataforma de acuerdo con las Condiciones de uso.",
      },
      {
        title: "Uso permitido",
        text: "Los visitantes pueden navegar, buscar y compartir enlaces a BioIndex. Por lo general, se permiten citas limitadas o capturas de pantalla con fines educativos, científicos o periodísticos, siempre que se atribuya claramente a BioIndex.",
      },
      {
        title: "Uso prohibido",
        text: "Sin autorización previa y escrita, no puedes reproducir, redistribuir, replicar, extraer, descargar masivamente, reutilizar comercialmente ni crear bases de datos u obras derivadas a partir de BioIndex o de su contenido original.",
      },
      {
        title: "Denuncias de infracción",
        text: (
          <>
            Si crees que material protegido por derechos de autor se ha utilizado
            en BioIndex sin autorización, contáctanos mediante la{" "}
            <Link
              to="/contact"
              className="font-medium text-[#01767A] underline"
            >
              página de contacto de BioIndex
            </Link>{" "}
            para que podamos revisar el asunto oportunamente.
          </>
        ),
      },
      {
        title: "Cambios en este Aviso",
        text: "Este Aviso de derechos de autor puede actualizarse a medida que evolucione BioIndex. La versión más reciente siempre estará disponible en esta página.",
      },
    ],
  },
  en: {
    title: "Copyright Notice",
    updated: "Last updated",
    introduction:
      "Unless otherwise indicated, the original software, design, and creative content comprising BioIndex™ are protected by copyright. User-uploaded content remains the property of its respective owners.",
    returnHome: "Return to BioIndex",
    rights: "All rights reserved.",
    sections: [
      {
        title: "BioIndex intellectual property",
        text: "The BioIndex™ interface, source code, database organization, original text, illustrations, badges, quizzes, logos, and other original creative assets are protected by copyright and other intellectual property laws.",
      },
      {
        title: "User-owned content",
        text: "Photographs and other original content uploaded by users remain the property of their respective owners. By uploading content, users grant BioIndex permission to display, organize, and use it within the platform in accordance with the Terms of Use.",
      },
      {
        title: "Permitted use",
        text: "Visitors may browse, search, and share links to BioIndex. Limited quotations or screenshots for educational, scientific, or journalistic purposes are generally permitted provided BioIndex is clearly credited.",
      },
      {
        title: "Prohibited use",
        text: "Without prior written permission, you may not reproduce, redistribute, mirror, scrape, bulk-download, commercially reuse, or create derivative databases or works from BioIndex or its original content.",
      },
      {
        title: "Reporting infringement",
        text: (
          <>
            If you believe copyrighted material has been used on BioIndex without
            authorization, please contact us through the{" "}
            <Link
              to="/contact"
              className="font-medium text-[#01767A] underline"
            >
              BioIndex contact page
            </Link>{" "}
            so the matter can be reviewed promptly.
          </>
        ),
      },
      {
        title: "Changes to this Notice",
        text: "This Copyright Notice may be updated as BioIndex evolves. The latest version will always be available on this page.",
      },
    ],
  },
};

export default function CopyrightPage() {
  const [language, setLanguage] = usePolicyLanguage();
  const content = copy[language];

  return (
    <main className="min-h-screen bg-[#374049] px-4 py-8 text-[#374049]">
      <section className="mx-auto max-w-5xl overflow-hidden rounded-3xl border border-[#93CA87]/40 bg-[#FCFFF6] shadow-lg">
        <div className="flex items-center justify-center bg-[#374049] px-6 py-6">
          <img
            src="/BioIndex Prototype Banner.png"
            alt="BioIndex"
            className="max-h-32 w-full max-w-md object-contain"
          />
        </div>

        <div className="px-6 py-8 sm:px-10 lg:px-14">
          <div className="flex justify-end">
            <PolicyLanguageToggle
              language={language}
              onChange={setLanguage}
            />
          </div>

          <div className="mt-6 max-w-3xl border-b border-[#374049]/10 pb-6">
            <h1
              className="text-4xl font-semibold"
              style={{ fontFamily: "Cormorant Garamond" }}
            >
              {content.title}
            </h1>

            <p className="mt-2 text-xs text-[#374049]/50">
              {content.updated}: {new Date().getFullYear()}
            </p>

            <p className="mt-5 text-sm leading-7 text-[#374049]/75">
              {content.introduction}
            </p>
          </div>

          <div className="mt-7 grid gap-4 md:grid-cols-2">
            {content.sections.map((section, index) => (
              <article
                key={section.title}
                className="rounded-2xl border border-[#374049]/10 bg-white/70 p-5 shadow-sm"
              >
                <div className="mb-3 flex items-center gap-3">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-[#24C3BE]/10 text-sm font-semibold text-[#24C3BE]">
                    {index + 1}
                  </span>

                  <h2 className="text-base font-semibold text-[#374049]">
                    {section.title}
                  </h2>
                </div>

                <div className="text-sm leading-7 text-[#374049]/70">
                  {section.text}
                </div>
              </article>
            ))}
          </div>

          <div className="mt-8 flex flex-col items-center border-t border-[#374049]/10 pt-7 text-center">
            <Link
              to="/"
              className="inline-flex items-center justify-center rounded-xl bg-[#374049] px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-[#2f373e]"
            >
              {content.returnHome}
            </Link>

            <p className="mt-5 text-xs leading-6 text-[#374049]/55">
              © {new Date().getFullYear()} BioIndex™. {content.rights}
            </p>
          </div>
        </div>
      </section>
    </main>
  );
}
